import { z } from 'zod';
import { roleSchema } from '../roles.js';
export const userStatusSchema = z.enum(['active', 'inactive', 'released']);
/**
 * The projection of a `users` document that every screen listing people needs:
 * a name, a role, and where they sit in the org. Deliberately not the soldier's
 * full personal file — that is a separate, narrower-audience read.
 *
 * `GET /api/me` returns exactly this shape for the caller themselves, so the
 * home screen and a roster row parse through the same schema.
 */
export const userSummarySchema = z.object({
    uid: z.string().min(1),
    personalNumber: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    rank: z.string().nullable(),
    title: z.string().nullable(),
    roles: z.array(roleSchema),
    teamId: z.string().nullable(),
    platoonId: z.string().nullable(),
    status: userStatusSchema,
});
/**
 * GET /api/units/:unitId/soldiers — the roster of one unit.
 *
 * Served by a Cloud Function rather than read directly from Firestore: scoping
 * a roster to the caller's chain of command needs a query the client cannot be
 * trusted to constrain, and an open `list` rule on `users` would be a
 * full-company read for anyone signed in.
 */
export const rosterResponseSchema = z.object({
    unitId: z.string().min(1),
    /** Sorted by last name then first name, in the server's locale-independent order. */
    soldiers: z.array(userSummarySchema),
    /** uid -> number of open assignments (§2.6 "soldier list with gear counts"). */
    openGear: z.record(z.string(), z.number().int().min(0)),
});
/**
 * Personal details (REQUIREMENTS §2.4, and §3.6 answered "keep the full set").
 *
 * This is the field set §2.10's information-security review is most likely to
 * cut, so it is one nested object rather than a dozen columns spread across the
 * user document — dropping a field then means deleting one line here and one
 * migration, not auditing every screen.
 *
 * Every field is nullable because a user created by an admin starts with almost
 * none of it filled in, and a half-complete profile must not block issuing gear.
 */
export const sizesSchema = z.object({
    shirt: z.string().nullable(),
    pants: z.string().nullable(),
    boots: z.string().nullable(),
    vest: z.string().nullable(),
});
export const emergencyContactSchema = z.object({
    name: z.string().nullable(),
    phone: z.string().nullable(),
    relation: z.string().nullable(),
});
export const personalDetailsSchema = z.object({
    /** ISO date, not a timestamp: a birth date has no meaningful time or zone. */
    dateOfBirth: z.string().nullable(),
    address: z.string().nullable(),
    bloodType: z.string().nullable(),
    sizes: sizesSchema,
    emergencyContact: emergencyContactSchema,
});
/**
 * GET /api/users/:uid — one soldier's full file.
 *
 * Strictly wider than `userSummarySchema` and strictly narrower in audience:
 * the caller must be the soldier themselves, a commander in their chain, or a
 * company-wide role. Enforced server-side, not by omitting a link.
 */
export const userDetailSchema = userSummarySchema.extend({
    email: z.string().nullable(),
    phone: z.string().nullable(),
    personalDetails: personalDetailsSchema,
    /** ISO-8601, or null when the document predates the field. */
    createdAt: z.string().nullable(),
    lastLoginAt: z.string().nullable(),
});
export const userDetailResponseSchema = z.object({ user: userDetailSchema });
/**
 * The patch shape for personal details.
 *
 * NOT `personalDetailsSchema.partial()`: that makes only the *top-level* keys
 * optional, leaving `sizes` requiring all four of its fields. A commander
 * correcting one boot size would have to resend the soldier's whole size chart,
 * and would get a 400 if they did not. The nesting has to be made partial at
 * every level it is patchable at.
 */
export const personalDetailsPatchSchema = z.object({
    dateOfBirth: z.string().nullable().optional(),
    address: z.string().nullable().optional(),
    bloodType: z.string().nullable().optional(),
    sizes: sizesSchema.partial().optional(),
    emergencyContact: emergencyContactSchema.partial().optional(),
});
/**
 * PATCH /api/users/:uid — edit a profile.
 *
 * Roles and status are deliberately absent: changing either is a different
 * capability with a different audit entry, and folding them in here would mean
 * one endpoint where a profile edit could quietly carry a privilege escalation.
 * They have their own endpoints below.
 */
export const updateUserRequestSchema = z
    .object({
    firstName: z.string().min(1).max(80),
    lastName: z.string().min(1).max(80),
    rank: z.string().max(40).nullable(),
    title: z.string().max(80).nullable(),
    phone: z.string().max(40).nullable(),
    personalDetails: personalDetailsPatchSchema,
    /**
     * Moving a soldier to another unit: a team, or a platoon itself — a
     * soldier need not belong to a team (owner, 2026-09-13, story 32). The
     * server derives `teamId`/`platoonId` from the target; the client never
     * sends them, so they cannot disagree. A platoon commander may move
     * soldiers within their own platoon; crossing platoons is an admin's call.
     */
    unitId: z.string().min(1),
})
    .partial()
    // An empty body is a no-op that would still write a ledger entry, which makes
    // the audit trail noisier without recording anything that happened.
    .refine((body) => Object.keys(body).length > 0, {
    message: 'At least one field must be supplied',
});
/**
 * PUT /api/users/:uid/roles — role assignment.
 *
 * Admin only. A csm is also level 3 but explicitly cannot change
 * roles or org structure (§2.2), which is the whole reason level is not the
 * only thing checked anywhere in this app.
 */
export const updateRolesRequestSchema = z.object({
    roles: z.array(roleSchema).min(1).max(6),
});
/**
 * POST /api/users/:uid/status — activate, deactivate, or mark released.
 *
 * Deactivation is refused while the soldier still holds open assignments
 * (§2.7). The app does not permit silent departures with a rifle unaccounted
 * for, so the refusal names what is still out.
 */
export const setUserStatusRequestSchema = z.object({
    status: userStatusSchema,
    reason: z.string().max(500).nullable().optional(),
});
/**
 * What a deactivation refusal reports back, so the UI can list the blockers.
 *
 * Carries the catalog item's display names, not just its id. The point of the
 * refusal is to tell a csm what to go and collect; "cat-helmet" is a
 * puzzle, "קסדה" is an instruction. Names are nullable because a blocker whose
 * catalog entry has since been deleted must still appear in the list — losing
 * the row would be far worse than losing its name.
 */
export const openAssignmentSchema = z.object({
    assignmentId: z.string(),
    catalogItemId: z.string(),
    nameHe: z.string().nullable(),
    nameEn: z.string().nullable(),
    inventoryUnitId: z.string().nullable(),
    quantity: z.number(),
    status: z.string(),
});
/**
 * Firebase's own floor is six characters; eight is the app's, and the cap
 * keeps a pasted novel out of the request. No composition rules: length is
 * what matters and rules push people towards "Password1!".
 */
export const passwordSchema = z.string().min(8).max(128);
/**
 * POST /api/users — create one account. Admin only (§2.7).
 *
 * Two ways in (owner, 2026-09-13). With no `password`, the account is created
 * with an unguessable random one purely so that Firebase's password provider
 * is attached; the client then asks Firebase to email a set-your-password
 * link (`sendPasswordResetEmail`), which is the invite. With a `password`,
 * the admin hands it over themselves — for a soldier with no working mailbox
 * — and no invite is sent. Either way the server never stores or returns it.
 */
export const createUserRequestSchema = z.object({
    email: z.string().trim().email().max(254),
    personalNumber: z
        .string()
        .trim()
        .regex(/^[0-9]{5,10}$/, 'A personal number is 5 to 10 digits'),
    firstName: z.string().trim().min(1).max(80),
    lastName: z.string().trim().min(1).max(80),
    rank: z.string().trim().max(40).nullable().default(null),
    title: z.string().trim().max(80).nullable().default(null),
    phone: z.string().trim().max(40).nullable().default(null),
    /** A team, or a platoon for a soldier with no team (story 32). */
    unitId: z.string().min(1),
    roles: z.array(roleSchema).min(1).max(6).default(['soldier']),
    /** Optional at creation; the same shape the profile edit patches. */
    personalDetails: personalDetailsPatchSchema.optional(),
    password: passwordSchema.nullable().default(null),
});
/** POST /api/users/:uid/password — an admin sets a soldier's password (owner, 2026-09-13). */
export const setPasswordRequestSchema = z.object({ password: passwordSchema });
export const setPasswordResponseSchema = z.object({ ok: z.literal(true) });
//# sourceMappingURL=users.js.map